import { flightSearchUrl, shortDate, weatherStrip } from './enrichment.js'

// The weather strip — P5-3 T10, HANDOFF screen 2 item 5.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 "NEVER PRESENT AS A FORECAST" — HANDOFF'S OWN BOLD, AND THE WHOLE RULE
// ═══════════════════════════════════════════════════════════════════════════
//
// Its source line is written out in the brief: *"ten-year averages for that
// week, not a forecast — it's 8 months out · open-meteo, fetched 16 Aug"*.
//
// Measured 2026-08-21: Open-Meteo's forecast window is about **sixteen days**,
// so `normals` is the ORDINARY case for a trip and `forecast` is the rare one.
// That is the opposite of what a weather widget usually assumes, and it is why
// the mode arrives as a field from the service rather than being inferred
// here: a component that guessed would guess "forecast", which is the one
// answer the brief forbids.
//
// ⚠️ THE SOURCE LINE IS NOT SMALL PRINT. It is rendered as text, unconditionally,
// in the same block as the bars — the same rule the three honesty lines follow
// on cost, for the same reason: a caveat you have to hover for is not a caveat.
//
// ═══════════════════════════════════════════════════════════════════════════
// FLIGHTS — A LINK, AND TR5 SAYS THAT IS A RULING
// ═══════════════════════════════════════════════════════════════════════════
//
// *"Flight prices are deep links, never scraped numbers … a scraped price is a
// lie with a decimal point."* So this renders an `<a>` and nothing else. No
// price, no "from £X", no fetch — there is no flight host in the service's
// allow-list and no flight endpoint on it.
//
// 🔴 THE PLACE COMES FROM THE SERVICE'S ANSWER, NOT FROM A RULE REPEATED HERE.
// *Which* place a trip's weather (and therefore its flight search) is about is
// a real decision — the chosen destination, or the only one, or the trip's own
// name when there are none, and NOTHING when two are still being compared.
// `routes/enrichment.py::_weather_place` owns it, and it reports which place it
// used. A second implementation in this component is CLAUDE.md gotcha 21's
// exact shape: two readings of one contract, agreeing until they do not.

/** Bar height as a percentage of the warmest day in this range.
 *
 * Scaled within the strip, never against an absolute temperature scale —
 * HANDOFF scales the compare screen's category bars the same way and for the
 * same reason: an absolute scale makes a mild week look like nothing happened.
 */
function heights(days) {
  const highs = days.map((day) => day.high_c).filter((value) => typeof value === 'number')
  if (highs.length === 0) return days.map(() => 0)
  const top = Math.max(...highs)
  const floor = Math.min(...highs) - 2
  const span = top - floor || 1
  return days.map((day) =>
    typeof day.high_c === 'number'
      ? Math.max(8, Math.round(((day.high_c - floor) / span) * 100))
      : 0,
  )
}

export default function WeatherStrip({ weather, range }) {
  const strip = weatherStrip(weather)

  if (strip.state !== 'ok') {
    // §8: *"the field shows a labeled absence. Never blocks, never
    // blank-that-looks-fine."* The `none` wordings are facts about the trip
    // ("weather once the dates are settled"); `unavailable` is the amber
    // report of a provider. `enrichment.js` decides which; this renders it.
    return (
      <p
        className={
          strip.state === 'unavailable'
            ? 'd2d-weather__absent d2d-weather__absent--warn d2d-micro'
            : 'd2d-weather__absent d2d-micro'
        }
        data-testid="weather-absent"
      >
        {strip.label}
      </p>
    )
  }

  const bars = heights(strip.days)
  const flights = flightSearchUrl({
    place: weather?.place,
    start: range?.start,
    end: range?.end,
  })

  return (
    <section className="d2d-weather" aria-label="Weather for the trip" data-testid="weather">
      <ol className="d2d-weather__bars">
        {strip.days.map((day, index) => (
          <li
            key={day.date}
            className={
              typeof day.rain_mm === 'number' && day.rain_mm >= 1
                ? 'd2d-weather__bar d2d-weather__bar--wet'
                : 'd2d-weather__bar'
            }
            data-testid={`weather-day-${day.date}`}
          >
            <span className="d2d-weather__fill" style={{ height: `${bars[index]}%` }} />
            <span className="d2d-weather__label d2d-micro">
              {typeof day.high_c === 'number' ? `${Math.round(day.high_c)}°` : '—'}
            </span>
            <span className="d2d-sr-only">{shortDate(day.date)}</span>
          </li>
        ))}
      </ol>

      {strip.summary ? (
        <p className="d2d-weather__summary" data-testid="weather-summary">
          {strip.summary}
        </p>
      ) : null}

      {/* 🔴 Always rendered, never behind a control. The mode is the claim. */}
      <p className="d2d-weather__source d2d-micro" data-testid="weather-source">
        {strip.source}
      </p>

      {flights ? (
        <p className="d2d-weather__flights d2d-micro">
          <a
            href={flights}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="flight-deeplink"
          >
            Look up flights to {weather.place}
          </a>{' '}
          {/* Said out loud, because a link that looked like a quote would be
              the "lie with a decimal point" the ruling is about. */}
          — we don’t quote prices
        </p>
      ) : null}
    </section>
  )
}
